import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

interface FantasyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

const EliminatorPage: React.FC = () => {
  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    fetchEliminatorLeagues();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  };

  const fetchEliminatorLeagues = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getFantasyLeagues();

      if (data.success) {
        const eliminatorLeagues = data.tournaments.filter(
          (league: FantasyLeague) => league.league_type === 'eliminator'
        );
        setLeagues(eliminatorLeagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching eliminator leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = () => {
    if (!isLoggedIn) {
      navigate('/tournaments');
      return;
    }
    navigate('/create-league?type=eliminator');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">💀</div>
        <h1 className="text-4xl font-bold text-white mb-4">Eliminator Tournaments</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          The ultimate survival challenge! Make one wrong prediction and you're eliminated. 
          Only the most accurate predictors will survive until the end.
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">How Eliminator Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎯</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Make Predictions</h3>
            <p className="text-white/70">Predict match outcomes with extreme precision. Every prediction counts!</p>
          </div>
          <div className="text-center">
            <div className="bg-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Risk Elimination</h3>
            <p className="text-white/70">One wrong prediction and you're out. The stakes are incredibly high!</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👑</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Last Survivor Wins</h3>
            <p className="text-white/70">The last player standing takes all the glory and prizes!</p>
          </div>
        </div>
      </div>

      {/* Elimination Rules */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Elimination Rules</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Prediction Types</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Match Winner:</strong> Predict the winning team</li>
              <li>• <strong>Total Runs:</strong> Predict total runs scored</li>
              <li>• <strong>Top Scorer:</strong> Predict the highest run scorer</li>
              <li>• <strong>Wicket Count:</strong> Predict total wickets taken</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Elimination Conditions</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Wrong Prediction:</strong> Immediate elimination</li>
              <li>• <strong>No Prediction:</strong> Automatic elimination</li>
              <li>• <strong>Tie Breaker:</strong> Closest prediction survives</li>
              <li>• <strong>Final Round:</strong> Last survivor wins</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Strategy Guide */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Survival Strategy</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Conservative Approach</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Safe Predictions:</strong> Choose obvious favorites</li>
              <li>• <strong>Research Form:</strong> Study recent team performance</li>
              <li>• <strong>Weather Check:</strong> Consider pitch and weather conditions</li>
              <li>• <strong>Head-to-Head:</strong> Check historical matchups</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Risk Management</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Diversify Predictions:</strong> Don't put all eggs in one basket</li>
              <li>• <strong>Monitor Others:</strong> Watch what other players predict</li>
              <li>• <strong>Timing:</strong> Make predictions close to match start</li>
              <li>• <strong>Stay Calm:</strong> Don't panic under pressure</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tournament Phases */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Tournament Phases</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-green-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold">1</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Group Stage</h3>
            <p className="text-white/70">Multiple matches, multiple chances to survive. Build momentum early.</p>
          </div>
          <div className="text-center">
            <div className="bg-yellow-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold">2</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Knockout Phase</h3>
            <p className="text-white/70">High-stakes matches. One wrong move and you're eliminated.</p>
          </div>
          <div className="text-center">
            <div className="bg-red-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold">3</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Final Showdown</h3>
            <p className="text-white/70">The last few survivors battle it out for ultimate glory.</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <button
          onClick={handleCreateLeague}
          className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-800 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-all transform hover:scale-105"
        >
          Create Eliminator League
        </button>
        <Link
          to="/tournaments"
          className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg text-lg font-semibold border border-white/20 transition-all text-center"
        >
          Browse All Tournaments
        </Link>
      </div>
    </div>
  );
};

export default EliminatorPage; 